import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Descriptions, Badge, Tooltip } from 'antd';
import dayjs from 'dayjs';

import { YYYY_MM_DD } from 'constants/wallet';
import { isUserAuthorized } from 'utils/helpers';
import { ACL_MATRIX } from 'common/permissions';
import { getFinancialMetrics } from 'services/business';
import { formatMoney } from 'utils/wallet';
import { getFormattedNumber } from 'utils/number';

import BRButton from 'components/BRButton/BRButton';
import StatCard from '../StatCard/StatCard';
import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';
import { notify } from 'components/Notify/Notify';

import { ReactComponent as WalletIcon } from 'assets/bosta-icons/wallet-02.svg';
import { ReactComponent as InfoIcon } from 'assets/bosta-icons/Info-Circle.svg';

import './FinancialMetrics.less';

const FinancialMetrics = ({ businessId, dateValue, ...props }) => {
  const { formatMessage } = useIntl();
  const { Item } = Descriptions;

  const [financialMetricsData, setFinancialMetricsData] = useState({});
  const [isFinancialMetricsLoading, setIsFinancialMetricsLoading] =
    useState(false);

  const startOfMonth = dayjs(dateValue).startOf('month').format(YYYY_MM_DD);
  const endOfMonth = dayjs(dateValue).endOf('month').format(YYYY_MM_DD);

  useEffect(() => {
    if (!dateValue || !businessId) {
      setFinancialMetricsData({});
      return;
    }

    const fetchFinancialMetrics = async () => {
      try {
        setIsFinancialMetricsLoading(true);
        const queryParams = {
          businessId,
          date: dateValue?.format('YYYY-MM-DD')
        };
        const { data } = await getFinancialMetrics({ ...queryParams });

        setFinancialMetricsData(data);
      } catch (error) {
        notify(error);
      } finally {
        setIsFinancialMetricsLoading(false);
      }
    };

    fetchFinancialMetrics();
  }, [dateValue, businessId]);

  const progressLegendData = [
    {
      text: formatMessage(
        { id: 'merchant_analysis.financial_metrics.in_progress' },
        { value: financialMetricsData?.compensationRequests?.inProgress || 0 }
      ),
      color: '#FFAB00'
    },
    {
      text: formatMessage(
        { id: 'merchant_analysis.financial_metrics.rejected' },
        { value: financialMetricsData?.compensationRequests?.rejected || 0 }
      ),
      color: '#FF5630'
    },
    {
      text: formatMessage(
        { id: 'merchant_analysis.financial_metrics.approved' },
        { value: financialMetricsData?.compensationRequests?.approved || 0 }
      ),
      color: '#00A76F'
    }
  ];

  const evaluateProgressBar = (value) => {
    return !value
      ? 0
      : (value /
          financialMetricsData?.compensationRequests
            ?.totalCompensationRequests) *
          100;
  };

  const checkProgressBarValues = () => {
    if (!financialMetricsData?.compensationRequests) return false;

    return Object.values(financialMetricsData?.compensationRequests).some(
      (value) => value > 0
    );
  };

  return (
    <LoadingWrapper loading={isFinancialMetricsLoading}>
      <StatCard
        title={formatMessage({
          id: 'merchant_analysis.financial_metrics.title'
        })}
        titleIcon={<WalletIcon className="br-financial-metrics-icon" />}
        className="br-financial-metrics-card"
      >
        <Descriptions
          className="br-financial-metrics"
          column={4}
          bordered={true}
        >
          <Item span={4} className="br-financial-metrics__progress">
            <div className="br-financial-metrics__requests-summary">
              <div className="br-financial-metrics__requests-summary-header">
                <span className="br-financial-metrics__requests-label subheading">
                  {formatMessage(
                    {
                      id: 'merchant_analysis.financial_metrics.total_compensation'
                    },
                    {
                      value:
                        financialMetricsData?.compensationRequests
                          ?.totalCompensationRequests || 0
                    }
                  )}
                </span>
                {businessId && (
                  <BRButton
                    type={'basic'}
                    className={'button-sm'}
                    label={formatMessage({ id: 'common.view' })}
                    onClick={() =>
                      window.open(
                        `wallet/compensation/all?businessId=${businessId}&startSubmittedDate=${startOfMonth}&endSubmittedDate=${endOfMonth}`,
                        '_blank'
                      )
                    }
                  />
                )}
              </div>
              {!checkProgressBarValues() ? (
                <div className="br-financial-metrics__progress-bar">
                  <span
                    className=" br-financial-metrics__progress-bar-empty-state"
                    style={{
                      width: '100%'
                    }}
                  />
                </div>
              ) : (
                <div className="br-financial-metrics__progress-bar">
                  <span
                    className="  br-financial-metrics__progress-bar-in-progress"
                    style={{
                      width: `${evaluateProgressBar(
                        financialMetricsData?.compensationRequests?.inProgress
                      )}%`
                    }}
                  />
                  <span
                    className=" br-financial-metrics__progress-bar-rejected"
                    style={{
                      width: `${evaluateProgressBar(
                        financialMetricsData?.compensationRequests?.rejected
                      )}%`
                    }}
                  />
                  <span
                    className="  br-financial-metrics__progress-bar-approved"
                    style={{
                      width: `${evaluateProgressBar(
                        financialMetricsData?.compensationRequests?.approved
                      )}%`
                    }}
                  />
                </div>
              )}
              <div className=" br-financial-metrics__legend">
                {progressLegendData?.map(({ text, color }, index) => (
                  <Badge color={color} text={text} key={index} />
                ))}
              </div>
            </div>
          </Item>

          <Item span={2}>
            <div className="br-financial-metrics__item">
              <span className="br-financial-metrics__label">
                {formatMessage({
                  id: 'merchant_analysis.financial_metrics.approved_compensation'
                })}
              </span>
              <span className="br-financial-metrics__value">
                {getFormattedNumber(
                  financialMetricsData?.compensationRequests?.approved
                )}
              </span>
            </div>
          </Item>

          <Item span={2}>
            <div className="br-financial-metrics__item">
              <span className="br-financial-metrics__label">
                {formatMessage({
                  id: 'merchant_analysis.financial_metrics.amount_compensated'
                })}
              </span>
              <span className="br-financial-metrics__value">
                {formatMoney(
                  financialMetricsData?.compensationRequests
                    ?.totalAmountCompensated
                )}
              </span>
            </div>
          </Item>
          <Item span={2}>
            <div className="br-financial-metrics__item">
              <span className="br-financial-metrics__label">
                {formatMessage({
                  id: 'merchant_analysis.financial_metrics.avg_compensation_amount'
                })}
              </span>
              <span className="br-financial-metrics__value">
                {formatMoney(
                  financialMetricsData?.compensationRequests
                    ?.averageAmountCompensated
                )}
              </span>
            </div>
          </Item>
          <Item span={2}>
            <div className="br-financial-metrics__item">
              <span className="br-financial-metrics__label">
                {formatMessage({
                  id: 'merchant_analysis.financial_metrics.percentage_compensated'
                })}
              </span>
              <span className="br-financial-metrics__value">
                {formatMessage(
                  { id: 'common.percentage' },
                  {
                    value:
                      financialMetricsData?.compensationRequests
                        ?.percentageCompensated
                  }
                )}
              </span>
            </div>
          </Item>
          {isUserAuthorized([], [ACL_MATRIX.BUSINESS_ANALYTICS_VIEW]) && (
            <>
              <Item span={2}>
                <div className="br-financial-metrics__item">
                  <span className="br-financial-metrics__label">
                    {formatMessage({
                      id: 'merchant_analysis.financial_metrics.total_revenue'
                    })}
                    <Tooltip
                      title={formatMessage({
                        id: 'merchant_analysis.financial_metrics.revenue_tooltip'
                      })}
                      placement={'right'}
                      overlayClassName="br-financial-metrics__revenue-tooltip"
                      color="#1C2A3A"
                    >
                      <InfoIcon className="info-icon" />
                    </Tooltip>
                  </span>
                  <span className="br-financial-metrics__value">
                    {formatMoney(financialMetricsData?.revenue?.totalRevenue)}
                  </span>
                </div>
              </Item>

              <Item span={2}>
                <div className="br-financial-metrics__item">
                  <span className="br-financial-metrics__label">
                    {formatMessage({
                      id: 'merchant_analysis.financial_metrics.avg_selling_rate'
                    })}
                  </span>
                  <span className="br-financial-metrics__value">
                    {formatMoney(
                      financialMetricsData?.revenue?.averageSellingRate
                    )}
                  </span>
                </div>
              </Item>
            </>
          )}
        </Descriptions>
      </StatCard>
    </LoadingWrapper>
  );
};

export default FinancialMetrics;
